import {
    Box,
    Typography,
    Container,
    useMediaQuery,
    Card,
    CardContent,
    Chip
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useEffect, useCallback } from "react";
import CountUp from 'react-countup';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { useLanguage } from "../../context/LanguageContext";

const WhyChooseUs = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const controls = useAnimation();
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true
    });
    const { t } = useLanguage();

    const primaryColor = '#FF6D00';
    const secondaryColor = '#FF9100';
    const lightOrange = '#FFAB40';
    const accentColor = '#E65100';

    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    const stats = [
        {
            value: 500,
            label: "线上录制美业经商视频",
            valueColor: primaryColor,
            icon: "🎥",
            description: "专业制作的高质量视频内容"
        },
        {
            value: 20,
            label: "品牌和学院合作方",
            valueColor: secondaryColor,
            icon: "🤝",
            description: "权威机构深度合作"
        },
        {
            value: 10,
            label: "美业细分赛道的内容",
            valueColor: lightOrange,
            icon: "🎯",
            description: "覆盖全行业领域"
        },
        {
            value: 30,
            label: "线下技术课程",
            valueColor: accentColor,
            icon: "📚",
            description: "实战技能培训"
        },
        {
            value: 5000,
            label: "马新参与过美业经商管理学习老板",
            valueColor: primaryColor,
            icon: "👥",
            description: "成功企业家学员群体"
        },
        {
            value: 10,
            label: "实战导师进行分享",
            valueColor: secondaryColor,
            icon: "🎓",
            description: "行业专家亲自指导"
        },
    ];

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
    }, [controls, inView]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: isMobile ? 0.15 : 0.1,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: {
            y: 40,
            opacity: 0,
            scale: 0.95
        },
        visible: {
            y: 0,
            opacity: 1,
            scale: 1,
            transition: {
                type: "spring",
                stiffness: 120,
                damping: 20,
                duration: 0.6
            }
        }
    };

    return (
        <Box
            id="stats"
            ref={ref}
            sx={{
                position: 'relative',
                minHeight: 'auto',
                py: { xs: 6, sm: 10, md: 12 },
                background: `
                    linear-gradient(135deg, 
                    rgba(255, 248, 242, 0.98) 0%, 
                    rgba(255, 255, 255, 1) 50%,
                    rgba(248, 250, 252, 0.98) 100%)
                `,
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: `
                        radial-gradient(circle at 15% 25%, ${lightOrange}08 0%, transparent 25%),
                        radial-gradient(circle at 85% 75%, ${secondaryColor}06 0%, transparent 20%)
                    `,
                    zIndex: 0
                }
            }}
        >
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 0
            }}>
                <Particles
                    id="tsparticles-stats"
                    init={particlesInit}
                    options={{
                        fpsLimit: 60,
                        particles: {
                            color: { value: [primaryColor, secondaryColor] },
                            links: {
                                color: secondaryColor,
                                distance: 120,
                                enable: true,
                                opacity: 0.1,
                                width: 1
                            },
                            move: {
                                direction: "none",
                                enable: true,
                                outMode: "bounce",
                                speed: 0.2,
                                straight: false
                            },
                            number: {
                                density: { enable: true, value_area: 1000 },
                                value: isMobile ? 15 : 40
                            },
                            opacity: { value: 0.2 },
                            shape: { type: "circle" },
                            size: {
                                random: true,
                                value: { min: 1, max: 2 }
                            }
                        },
                        detectRetina: true
                    }}
                />
            </Box>

            <Container
                maxWidth="xl"
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    px: { xs: 2, sm: 3, md: 4 }
                }}
            >
                <motion.div
                    initial="hidden"
                    animate={controls}
                    variants={containerVariants}
                >
                    {/* Header Section */}
                    <Box sx={{
                        textAlign: 'center',
                        mb: { xs: 5, sm: 7, md: 8 },
                        maxWidth: '900px',
                        mx: 'auto',
                        px: { xs: 1, sm: 0 }
                    }}>
                        <motion.div variants={itemVariants}>
                            <Chip
                                label="为什么选择我们"
                                sx={{
                                    mb: { xs: 2, sm: 3, md: 4 },
                                    px: { xs: 3, sm: 3, md: 4 },
                                    py: { xs: 1, sm: 1.5 },
                                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                                    fontWeight: 600,
                                    background: `linear-gradient(45deg, ${primaryColor}15, ${secondaryColor}15)`,
                                    color: primaryColor,
                                    border: `1px solid ${primaryColor}25`,
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: 2
                                }}
                            />
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <Typography
                                variant={isMobile ? "h4" : "h2"}
                                component="h2"
                                sx={{
                                    fontWeight: 800,
                                    letterSpacing: '-0.01em',
                                    lineHeight: { xs: 1.2, sm: 1.1 },
                                    mb: { xs: 2, sm: 3, md: 4 },
                                    color: theme.palette.text.primary,
                                    fontSize: {
                                        xs: '1.75rem',
                                        sm: '2.5rem',
                                        md: '3.25rem'
                                    }
                                }}
                            >
                                {t('whyChooseUs.title')}
                            </Typography>
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.6,
                                    fontWeight: 400,
                                    fontSize: {
                                        xs: '0.9rem',
                                        sm: '1.125rem',
                                        md: '1.25rem'
                                    }
                                }}
                            >
                                {t('whyChooseUs.subtitle')}
                            </Typography>
                        </motion.div>
                    </Box>

                    {/* Stats Grid - Using CSS Grid for precise control */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(3, 1fr)'
                            },
                            gap: { xs: 3, sm: 4 },
                            width: '100%',
                            maxWidth: '1200px',
                            mx: 'auto',
                            mb: { xs: 5, sm: 7, md: 8 }
                        }}
                    >
                        {stats.map((stat, index) => (
                            <motion.div
                                key={index}
                                variants={itemVariants}
                                whileHover={{
                                    scale: isMobile ? 1.01 : 1.02,
                                    transition: { duration: 0.2 }
                                }}
                            >
                                <Card
                                    sx={{
                                        height: '100%',
                                        width: '100%',
                                        minHeight: { xs: '200px', sm: '260px' },
                                        borderRadius: { xs: 3, sm: 3 },
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(20px)',
                                        border: `1px solid ${stat.valueColor}15`,
                                        boxShadow: isMobile
                                            ? `0 4px 20px ${stat.valueColor}06`
                                            : `0 8px 32px ${stat.valueColor}08`,
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '3px',
                                            background: `linear-gradient(90deg, ${stat.valueColor}, ${stat.valueColor}60)`,
                                            zIndex: 1
                                        },
                                        '&:hover': {
                                            transform: isMobile ? 'translateY(-2px)' : 'translateY(-4px)',
                                            boxShadow: isMobile
                                                ? `0 6px 24px ${stat.valueColor}10`
                                                : `0 12px 48px ${stat.valueColor}15`,
                                            '& .stat-icon': {
                                                transform: isMobile ? 'scale(1.05)' : 'scale(1.1)'
                                            }
                                        }
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            p: { xs: 2, sm: 3 },
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <Box
                                            className="stat-icon"
                                            sx={{
                                                fontSize: {
                                                    xs: '2rem',
                                                    sm: '3rem'
                                                },
                                                mb: { xs: 1, sm: 2 },
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            {stat.icon}
                                        </Box>

                                        <Typography
                                            variant="h3"
                                            sx={{
                                                fontWeight: 900,
                                                mb: { xs: 0.5, sm: 1.5 },
                                                lineHeight: 1,
                                                color: stat.valueColor,
                                                fontSize: {
                                                    xs: '1.75rem',
                                                    sm: '2.5rem'
                                                }
                                            }}
                                        >
                                            {inView && (
                                                <CountUp
                                                    end={stat.value}
                                                    duration={2.5}
                                                    suffix={stat.value >= 10 ? "+" : ""}
                                                    delay={index * 0.1}
                                                />
                                            )}
                                        </Typography>

                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: 600,
                                                lineHeight: 1.3,
                                                mb: { xs: 0.5, sm: 1.5 },
                                                fontSize: {
                                                    xs: '0.8rem',
                                                    sm: '1rem'
                                                }
                                            }}
                                        >
                                            {stat.label}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                fontWeight: 400,
                                                lineHeight: 1.4,
                                                fontSize: {
                                                    xs: '0.7rem',
                                                    sm: '0.8125rem'
                                                }
                                            }}
                                        >
                                            {stat.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </Box>

                    {/* Bottom CTA Section */}
                    <motion.div variants={itemVariants}>
                        <Box
                            sx={{
                                textAlign: 'center',
                                p: { xs: 2.5, sm: 4 },
                                borderRadius: { xs: 3, sm: 3 },
                                background: `linear-gradient(135deg, 
                                    rgba(255, 109, 0, 0.04) 0%, 
                                    rgba(255, 145, 0, 0.06) 100%)`,
                                backdropFilter: 'blur(10px)',
                                border: `1px solid ${primaryColor}15`,
                                maxWidth: '800px',
                                mx: 'auto'
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.palette.text.primary,
                                    mb: { xs: 1.5, sm: 3 },
                                    fontSize: {
                                        xs: '1.1rem',
                                        sm: '1.5rem'
                                    }
                                }}
                            >
                                准备开始您的美业创业之旅？
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.6,
                                    fontSize: {
                                        xs: '0.8rem',
                                        sm: '1rem'
                                    }
                                }}
                            >
                                加入我们的专业培训体系，获得行业领先的技术指导和商业智慧
                            </Typography>
                        </Box>
                    </motion.div>
                </motion.div>
            </Container>
        </Box>
    );
};

export default WhyChooseUs;